import { BackArrow } from 'assets/icons';
import { PinLocationIcon, WarningExclamationIcon } from 'assets/icons/app';
import Button from 'components/atoms/Button';
import Footer from 'components/molecules/Footer';
import { BUTTON_NAMES } from 'constant/button-names';
import { useGlobalConfig } from 'pages/queries/global.queries';
import * as React from 'react';
import { useMemo } from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/esm/Row';
import Spinner from 'react-bootstrap/Spinner';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { calculateDistance } from 'utils/map';

const StyledBottomContainer = styled.div`
  width: 100%;
  position: absolute;
  bottom: 0;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  padding: 28px;
  padding-left: 24px;
`;

const StyledBottomContent = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.extraLightShadeGray};
  min-height: 170px;
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  box-shadow: 0px 0px 24px 0px #00000018;
  padding: 28px;
  padding-left: 24px;
  // space for footer button as that is sticky.
  padding-bottom: 90px;
`;

const StyledLocationText = styled.h1`
  color: ${({ theme }) => theme.colors.deepPurple};
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px; /* 120% */
  letter-spacing: 0.4px;
`;

const StyledInfoText = styled.p`
  color: ${({ theme }) => theme.colors.deepPurple};
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 21px; /* 150% */
  letter-spacing: 0.1px;
  height: 21px;
`;

const StyledInfoContainer = styled.div`
  border-radius: 6px;
  background-color: ${({ theme }) => theme.colors.ghostWhite};
  display: flex;
  align-items: center;
  height: 45px;
  padding-right: 20px;
  padding-left: 20px;
  gap: 10px;
`;

const StyledWarningText = styled.p`
  margin: 0px;
  color: ${({ theme }) => theme.colors.darkStaleGray};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
  letter-spacing: 0.1px;
`;
const StyledPinLocationIcon = styled(PinLocationIcon)<{ $isactive: 'true' | 'false' }>`
  position: absolute;
  top: -243px;
  right: 0px;

  > path {
    fill: ${({ theme, $isactive }) => ($isactive === 'true' ? theme.colors.deepPurple : theme.colors.transparent)};
    stroke: ${({ theme, $isactive }) => ($isactive === 'true' ? theme.colors.transparent : theme.colors.deepPurple)};
  }
`;
const StyledBackArrow = styled(BackArrow)`
  > path {
    fill: ${({ theme }) => theme.components.googleMapBottomSheet.backArrow.color};
  }
`;

const StyledBackButton = styled.button`
  style: none;
  background: ${({ theme }) => theme.components.onboardingBottomSheet.button.backgroundColor};
  border: ${({ theme }) =>
    `${theme.components.onboardingBottomSheet.button.border.width}px solid ${theme.components.onboardingBottomSheet.button.border.color}`};
  border-radius: 40px;
  height: 48px;
  padding: 0 25px;
`;

const StyledFooter = styled(Footer)`
  padding-bottom: 20px;
`;

const StyledButtonsRow = styled.div`
  display: flex;
  gap: 10px;
`;

interface MapFooterContainerProps {
  onPressConfirm: () => void;
  isAddressValid: boolean;
  status?: 'error' | 'idle' | 'loading' | 'success';
  currentPosition?: { lat: number; lng: number };
  mapCenter?: { lat: number; lng: number };
  onMoveToCurrentPosition?: () => void;
}

const MapFooterContainer = ({
  onPressConfirm,
  isAddressValid,
  onMoveToCurrentPosition,
  currentPosition,
  mapCenter,
  status,
}: MapFooterContainerProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useGlobalConfig();

  const renderConfirmButton = () => {
    if (status === 'loading') {
      return (
        <>
          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
          <span className="sr-only">{t('common.loading')}</span>
        </>
      );
    }
    if (status === 'error') {
      return <span>{t('common.locationNotSupported')}</span>;
    }
    return <span>{t('common.confirmPIN')}</span>;
  };

  const isCloseToCurrentLocation = useMemo(() => {
    if (!currentPosition || !mapCenter) return true;

    const distance = calculateDistance(currentPosition, mapCenter);
    if (distance <= parseInt(data?.nearByRadiusThreshold ?? '80', 10)) {
      return true;
    }

    return false;
  }, [currentPosition, mapCenter, data?.nearByRadiusThreshold]);

  const isCurrentPositionEnabled = useMemo(() => Boolean(currentPosition?.lat && currentPosition?.lng), [currentPosition]);

  const onPressBack = () => {
    navigate(-1);
  };

  return (
    <StyledBottomContainer>
      <StyledPinLocationIcon
        onClick={isCurrentPositionEnabled ? onMoveToCurrentPosition : undefined}
        $isactive={`${Boolean(isCurrentPositionEnabled)}`}
      />
      <Row>
        <Col>
          <StyledBottomContent>
            <StyledLocationText>
              {!isCloseToCurrentLocation ? t('mapScreen.needExactLocation') : t('mapScreen.pointYourExactLocation')}
            </StyledLocationText>
            <StyledInfoText>{t('mapScreen.helpDriverFindYouFaster')}</StyledInfoText>
            {!isCloseToCurrentLocation && (
              <StyledInfoContainer>
                <WarningExclamationIcon />
                <StyledWarningText>{t('mapScreen.youSeemsFarAway')}</StyledWarningText>
              </StyledInfoContainer>
            )}
          </StyledBottomContent>
        </Col>
      </Row>

      <StyledFooter showBackgroundColor={false} className="flex bg-transparent shadow-none">
        <StyledButtonsRow>
          <StyledBackButton onClick={onPressBack}>
            <StyledBackArrow />
          </StyledBackButton>
          <Button
            buttonName={BUTTON_NAMES.CONFIRM_LOCATION}
            size="lg"
            block
            disabled={!isAddressValid || status === 'loading'}
            variant={status === 'error' ? 'danger' : 'primary'}
            onClick={onPressConfirm}
            backgroundColor={theme.components.onboardingBottomSheet.button.mainBackgroundColor}
            textColor={theme.components.onboardingBottomSheet?.button?.textColor}
          >
            {renderConfirmButton()}
          </Button>
        </StyledButtonsRow>
      </StyledFooter>
    </StyledBottomContainer>
  );
};

export default MapFooterContainer;
